import { HostAriaLabelMixin, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

/** The viewport edge the tab hugs; the sliver's rounding faces away from it. */
export type EdgeTabEdge = 'left' | 'right' | 'top' | 'bottom'

/**
 * A slim tab at a viewport edge for a coarse pointer: 8×56 visible, a 40×64
 * hit target extending inward from it. A tap is a plain `click` on the host.
 * Owns no resting geometry — the consumer positions it flush with the edge
 * it names (`class="fixed right-0 bottom-24"`).
 *
 * `touch-action: manipulation`, never `none`: a swipe that starts on the tab
 * still scrolls the page, so the tab stays out of the way of it.
 */
@schmancyElement('schmancy-edge-tab')
export class SchmancyEdgeTab extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
			}
		`,
	]

	/** The accessible name — what a screen reader announces and a harness targets. Required. */
	@property({ type: String }) label?: string

	@property({ type: String, reflect: true }) edge: EdgeTabEdge = 'right'

	protected render(): unknown {
		const vertical = this.edge === 'left' || this.edge === 'right'
		return html`
			<button
				type="button"
				aria-label=${ifDefined(this.ariaLabel || this.label)}
				class=${this.classMap({
					'group flex cursor-pointer touch-manipulation select-none bg-transparent p-0 outline-hidden': true,
					'h-16 w-10 items-center': vertical,
					'h-10 w-16 justify-center': !vertical,
					'justify-end': this.edge === 'right',
					'justify-start': this.edge === 'left',
					'items-end': this.edge === 'bottom',
					'items-start': this.edge === 'top',
				})}
			>
				<span
					class=${this.classMap({
						'block bg-surface-onVariant/70 transition-colors duration-150 group-hover:bg-surface-onVariant group-active:bg-surface-onVariant outline-secondary-default group-focus-visible:outline-solid group-focus-visible:outline-2 group-focus-visible:outline-offset-2': true,
						'h-14 w-2': vertical,
						'h-2 w-14': !vertical,
						'rounded-l-full': this.edge === 'right',
						'rounded-r-full': this.edge === 'left',
						'rounded-t-full': this.edge === 'bottom',
						'rounded-b-full': this.edge === 'top',
					})}
				></span>
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-edge-tab': SchmancyEdgeTab
	}
}
